import { type KeyboardEvent, useEffect, useRef, useState } from 'react';

import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Text from '@cartwhl/ui/base/Text';
import ChevronDownIcon from '@cartwhl/ui/icons/ChevronDownIcon';
import PenIcon from '@cartwhl/ui/icons/PenIcon';
import PlusIcon from '@cartwhl/ui/icons/PlusIcon';
import Button from '@cartwhl/ui/input/Button';

import { useStore } from '../motionEditorStore';

export default function EditableSceneName({
  sceneID,
  sceneName,
  onPickNewMotion,
}: {
  sceneID: string;
  sceneName: string;
  onPickNewMotion?: () => void;
}) {
  const store = useStore();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(sceneName);
  const inputRef = useRef<HTMLInputElement>(null);
  const skipNextBlurCommitRef = useRef(false);

  useEffect(() => {
    setName(sceneName);
  }, [sceneName]);

  useEffect(() => {
    if (!isEditing || !inputRef.current) {
      return;
    }

    inputRef.current.focus();
    inputRef.current.setSelectionRange(0, inputRef.current.value.length);
  }, [isEditing]);

  const handleCommit = async () => {
    if (skipNextBlurCommitRef.current) {
      skipNextBlurCommitRef.current = false;
      return;
    }

    setIsEditing(false);
    const nextName = name.trim();

    if (!nextName) {
      setName(sceneName);
      return;
    }

    if (nextName === sceneName) {
      setName(sceneName);
      return;
    }

    setName(nextName);
    const didUpdate = await store.updateSceneName(sceneID, nextName);
    if (!didUpdate) {
      setName(sceneName);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.currentTarget.blur();
      return;
    }

    if (event.key === 'Escape') {
      skipNextBlurCommitRef.current = true;
      setName(sceneName);
      setIsEditing(false);
      event.currentTarget.blur();
    }
  };

  return (
    <div className="relative z-10 flex min-w-0 flex-1 items-center justify-center overflow-hidden">
      {isEditing ? (
        <input
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onBlur={() => {
            void handleCommit();
          }}
          onKeyDown={handleKeyDown}
          ref={inputRef}
          className="font-display outline-hidden w-full max-w-[420px] border-none bg-transparent text-center text-sm font-medium"
        />
      ) : (
        <Dropdown
          align="center"
          side="bottom"
          trigger={
            <Button
              variant="tertiary"
              icon={<ChevronDownIcon className="text-gray-60" />}
              alignIcon="end"
              className="max-w-full"
            >
              <Text variant="studio/title/md" className="truncate">
                {name || 'Untitled scene'}
              </Text>
            </Button>
          }
        >
          <Dropdown.Item
            icon={<PenIcon />}
            onClick={() => setIsEditing(true)}
          >
            Edit scene name
          </Dropdown.Item>
          {onPickNewMotion ? (
            <Dropdown.Item icon={<PlusIcon />} onClick={onPickNewMotion}>
              Pick new motion
            </Dropdown.Item>
          ) : null}
        </Dropdown>
      )}
    </div>
  );
}
